import Store from '../../Store';
import SDK from '../../Components/SDK';
import { UnreadCountUpdate, UnreadCountDelete, UnreadUserObj } from '../../Actions/UnreadCount';
import { getChatMessageHistoryById } from './ChatHelper';

const SERVER_SYNC_THROTTLE_MS = 600;

const sessions = new Map();

const normalizeTs = ts => {
    const n = Number(ts) || 0;
    return n.toString().length >= 16 ? Math.floor(n / 1000) : n;
};

const computeCount = session => Math.max(0, (session.serverUnread || 0) - session.seenUnread.size);

const getSeenReceiptFlag = () => {
    const { privacySettingsData } = Store.getState();
    return privacySettingsData?.seenReceipt === 1 ? '1' : '0';
};

const lookupTs = (chatId, msgId) => {
    if (!msgId) return null;
    const history = getChatMessageHistoryById(chatId) || [];
    const match = history.find(m => m && m.msgId === msgId);
    return match ? normalizeTs(match.timestamp) : null;
};

const resolveAnchorTs = (session, chatId) => {
    if (session.anchorTs != null) return session.anchorTs;
    const ts = lookupTs(chatId, session.anchorMsgId);
    if (ts != null) session.anchorTs = ts;
    return session.anchorTs;
};

const resolveLatestTs = (session, chatId) => {
    if (session.latestTs != null) return session.latestTs;
    const ts = lookupTs(chatId, session.latestMsgId);
    if (ts != null) session.latestTs = ts;
    return session.latestTs;
};

const writeStore = (chatId, session) => {
    const count = computeCount(session);
    Store.dispatch(
        UnreadCountUpdate({ fromUserId: chatId, count, isRefresh: true, source: 'unread-engine' }),
    );
    Store.dispatch(
        UnreadUserObj({
            fromUserId: chatId,
            count,
            serverUnread: session.serverUnread,
            anchorUnread: session.anchorUnread,
            anchorMsgId: session.anchorMsgId,
            latestMsgId: session.latestMsgId,
            fetchLastMsgId: session.anchorMsgId,
            chatJid: session.chatJid,
            groupId: session.groupId,
        }),
    );
    return count;
};

const sendServerSync = (chatId, session) => {
    if (!session.chatJid) return;
    const seenMsgId = session.newestSeenMsgId;
    if (!seenMsgId) return;
    SDK.sendSeenStatus(session.chatJid, seenMsgId, session.groupId || '', getSeenReceiptFlag());
    if (seenMsgId === session.latestMsgId) {
        SDK.updateRecentChatUnreadCount(session.chatJid, session.latestMsgId);
    }
};

const scheduleServerSync = (chatId, session) => {
    const now = Date.now();
    if (!session.lastSyncAt || now - session.lastSyncAt >= SERVER_SYNC_THROTTLE_MS) {
        session.lastSyncAt = now;
        sendServerSync(chatId, session);
        return;
    }
    if (session.syncTimer) return;
    const wait = SERVER_SYNC_THROTTLE_MS - (now - session.lastSyncAt);
    session.syncTimer = setTimeout(() => {
        session.syncTimer = null;
        const current = sessions.get(chatId);
        if (current) {
            current.lastSyncAt = Date.now();
            sendServerSync(chatId, current);
        }
    }, wait);
};

const flushPendingSyncs = () => {
    sessions.forEach((session, chatId) => {
        if (!session.syncTimer) return;
        clearTimeout(session.syncTimer);
        session.syncTimer = null;
        session.lastSyncAt = Date.now();
        sendServerSync(chatId, session);
    });
};

if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushPendingSyncs();
    });
}
if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushPendingSyncs);
}

const createSession = (chatId, { anchorMsgId, latestMsgId, serverUnread, chatJid, groupId }) => ({
    serverUnread: parseInt(serverUnread) || 0,
    anchorUnread: parseInt(serverUnread) || 0,
    anchorMsgId: anchorMsgId || '',
    anchorTs: null,
    latestMsgId: latestMsgId || '',
    latestTs: null,
    chatJid: chatJid || '',
    groupId: groupId || '',
    seenUnread: new Set(),
    watermarkTs: 0,
    newestSeenMsgId: '',
    syncTimer: null,
});

export const openChat = (chatId, opts = {}) => {
    if (!chatId) return 0;
    const existing = sessions.get(chatId);
    const merged = {
        ...opts,
        anchorMsgId: existing?.anchorMsgId || opts.anchorMsgId || '',
    };
    const unreadAtOpen = parseInt(opts.serverUnread) || 0;
    const session = createSession(chatId, merged);
    session.anchorUnread = unreadAtOpen;
    session.serverUnread = 0;
    sessions.set(chatId, session);
    resolveAnchorTs(session, chatId);
    resolveLatestTs(session, chatId);
    writeStore(chatId, session);
    return unreadAtOpen;
};

export const getSessionInfo = chatId => {
    const session = chatId && sessions.get(chatId);
    if (!session) return null;
    return {
        serverUnread: session.serverUnread || 0,
        anchorUnread: session.anchorUnread || 0,
        anchorMsgId: session.anchorMsgId || '',
        latestMsgId: session.latestMsgId || '',
    };
};

export const markSeen = (chatId, msgId, ts) => {
    const session = sessions.get(chatId);
    if (!session || !msgId || session.seenUnread.has(msgId)) return;

    const nts = normalizeTs(ts);
    const anchorTs = resolveAnchorTs(session, chatId);
    const boundaryTs = anchorTs != null ? anchorTs - 1 : null;
    if (boundaryTs != null && nts <= boundaryTs) return;

    session.seenUnread.add(msgId);
    if (nts > session.watermarkTs) {
        session.watermarkTs = nts;
        session.newestSeenMsgId = msgId;
    }

    if (session.latestMsgId && msgId === session.latestMsgId) {
        session.serverUnread = session.seenUnread.size;
    }

    writeStore(chatId, session);
};

const getRecentApiUnread = chatId => {
    const recents = Store.getState()?.recentChatData?.data;
    if (!Array.isArray(recents)) return 0;
    const entry = recents.find(e => {
        const id = (e?.fromUserId || '').includes('@') ? e.fromUserId.split('@')[0] : e?.fromUserId;
        return id === chatId;
    });
    return Number(entry?.unreadCount) || 0;
};

export const onLiveMessage = (chatId, { msgId, ts, chatJid, groupId, isActiveChat, isActiveInView } = {}) => {
    let session = sessions.get(chatId);
    const recentUnread = getRecentApiUnread(chatId);
    if (!session) {
        const badge = Store.getState()?.unreadCountData?.unreadDataObj?.[chatId]?.count || 0;
        session = createSession(chatId, { chatJid, groupId, serverUnread: Math.max(badge, recentUnread) });
        sessions.set(chatId, session);
    } else if (recentUnread > (session.serverUnread || 0)) {
        session.serverUnread = recentUnread;
    }
    if (chatJid && !session.chatJid) session.chatJid = chatJid;
    if (groupId && !session.groupId) session.groupId = groupId;

    const wasCaughtUp = (session.serverUnread || 0) === 0;
    if (!isActiveChat && wasCaughtUp && msgId) {
        session.anchorMsgId = msgId;
        session.anchorTs = ts != null ? normalizeTs(ts) : null;
    }

    session.serverUnread = (session.serverUnread || 0) + 1;
    session.latestMsgId = msgId || session.latestMsgId;
    session.latestTs = ts != null ? normalizeTs(ts) : session.latestTs;

    if (isActiveChat) session.anchorUnread = 0;

    if (isActiveInView) {
        markSeen(chatId, msgId, ts);
    } else {
        writeStore(chatId, session);
    }
};

export const markChatRead = chatId => {
    if (!chatId) return;
    const session = sessions.get(chatId);
    if (!session) {
        Store.dispatch(UnreadUserObj({ fromUserId: chatId, count: 0, anchorUnread: 0 }));
        Store.dispatch(UnreadCountDelete({ fromUserId: chatId }));
        return;
    }
    session.serverUnread = session.seenUnread.size;
    session.anchorUnread = 0;
    writeStore(chatId, session);
    scheduleServerSync(chatId, session);
};

// Called when an unread chat is opened. Sends the seen receipt for the latest
// message (so the sender sees "seen", not just "delivered") but keeps
// anchorUnread intact so the "N unread messages" divider still shows. Without
// this, the first open only updated the unread count and never advanced the
// seen watermark, so no receipt was sent until a later interaction.
export const flushSeenOnOpen = chatId => {
    if (!chatId) return;
    const session = sessions.get(chatId);
    if (!session || !session.chatJid || !session.latestMsgId) return;
    session.seenUnread.add(session.latestMsgId);
    session.newestSeenMsgId = session.latestMsgId;
    if (session.latestTs != null) session.watermarkTs = session.latestTs;
    scheduleServerSync(chatId, session);
};

// Called when the user actually views the bottom of an active chat (scrolled
// down or returned to the tab). Besides clearing the local count, it advances
// the seen watermark to the latest message and triggers the server sync, so a
// seen receipt is sent for messages that were only counted (not marked seen)
// while the user was scrolled up — otherwise they stay "delivered" on the
// sender's device.
export const markChatSeenToLatest = chatId => {
    if (!chatId) return;
    const session = sessions.get(chatId);
    if (!session) {
        Store.dispatch(UnreadUserObj({ fromUserId: chatId, count: 0, anchorUnread: 0 }));
        Store.dispatch(UnreadCountDelete({ fromUserId: chatId }));
        return;
    }
    if (session.latestMsgId) {
        session.seenUnread.add(session.latestMsgId);
        session.newestSeenMsgId = session.latestMsgId;
        if (session.latestTs != null) session.watermarkTs = session.latestTs;
    }
    session.serverUnread = 0;
    session.anchorUnread = 0;
    writeStore(chatId, session);
    scheduleServerSync(chatId, session);
};

export const syncServer = (chatId, opts = {}) => {
    if (!chatId) return;
    const existing = sessions.get(chatId);
    if (existing && existing.isActive) return;
    sessions.delete(chatId);
    openChat(chatId, opts);
    const session = sessions.get(chatId);
    if (session) session.isActive = false;
};

export const setActiveChat = chatId => {
    sessions.forEach(s => {
        s.isActive = false;
    });
    const session = chatId && sessions.get(chatId);
    if (session) session.isActive = true;
};

export const localClear = chatId => {
    if (!chatId) return;
    const session = sessions.get(chatId);
    if (session) {
        session.serverUnread = 0;
        session.seenUnread = new Set();
        if (session.syncTimer) {
            clearTimeout(session.syncTimer);
            session.syncTimer = null;
        }
    }
    Store.dispatch(UnreadUserObj({ fromUserId: chatId, count: 0 }));
    Store.dispatch(UnreadCountDelete({ fromUserId: chatId }));
};

export const clearRecentUnread = chatId => {
    if (chatId) Store.dispatch(UnreadCountDelete({ fromUserId: chatId }));
};

export const closeChat = chatId => {
    const session = chatId && sessions.get(chatId);
    if (session && session.syncTimer) clearTimeout(session.syncTimer);
    if (chatId) sessions.delete(chatId);
};

export const getCount = chatId => {
    const session = chatId && sessions.get(chatId);
    return session ? computeCount(session) : 0;
};

export default {
    openChat,
    markSeen,
    onLiveMessage,
    syncServer,
    setActiveChat,
    localClear,
    clearRecentUnread,
    closeChat,
    getCount,
};
